import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { AiOutlineDelete, AiOutlinePlus } from "react-icons/ai";
import CustomButton from "../../../../helperComponents/CustomButton";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 14px;
  background: linear-gradient(
    180deg,
    var(--surface) 0%,
    var(--translucentLight) 100%
  );
`;

const Top = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Title = styled.div`
  font-size: 13px;
  font-weight: 900;
  text-transform: uppercase;
  color: #555;
`;

const PlanningCard = styled.div`
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--surface);
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-shadow: var(--lightShadow);
`;

const PlanningTitle = styled.div`
  font-size: 13px;
  font-weight: 800;
  text-transform: uppercase;
  color: var(--element);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Grid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.div`
  font-size: 12px;
  font-weight: 700;
  opacity: 0.85;
`;

const TimeInput = styled.input`
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 11px 12px;
  background: var(--surface);
  color: var(--element);
  font-size: 14px;
  outline: none;
`;
const InputHint = styled.div`
  font-size: 11px;
  opacity: 0.7;
`;

const Hint = styled.div`
  font-size: 12px;
  color: #a35050;
  font-weight: 600;
`;

export default function ActivityPlanningsEditor({ value, onChange, label }) {
  const { t } = useTranslation();
  let plannings = normalizePlannings(value);

  return (
    <Container>
      <Top>
        <Title>{label || t("activityTypePlannings")}</Title>
        <CustomButton
          icon={<AiOutlinePlus />}
          style={{ background: "var(--surface)" }}
          onClick={addPlanning}
        >
          {t("activityTypeAddPlanning")}
        </CustomButton>
      </Top>

      {plannings.map((planning, index) => {
        let startMins = timeToMinutes(planning.startingTime);
        let endMins = timeToMinutes(planning.endingTime);
        let badRange =
          startMins !== null && endMins !== null && startMins >= endMins;

        return (
          <PlanningCard key={`planning-${index}`}>
            <PlanningTitle>
              {t("activityTypePlanning")} {index + 1}
              {plannings.length > 1 ? (
                <CustomButton
                  icon={<AiOutlineDelete />}
                  style={{ background: "var(--surface)" }}
                  onClick={removePlanning(index)}
                >
                  {t("remove")}
                </CustomButton>
              ) : null}
            </PlanningTitle>

            <Grid>
              <Field>
                <Label>{t("activityTypeStartTime")}</Label>
                <TimeInput
                  type="text"
                  placeholder="08:30"
                  value={asTimeValue(planning.startingTime)}
                  onChange={updateValue(index, "startingTime")}
                />
                <InputHint>{t("activityTypeTimeInputHint")}</InputHint>
              </Field>

              <Field>
                <Label>{t("activityTypeEndTime")}</Label>
                <TimeInput
                  type="text"
                  placeholder="17:45"
                  value={asTimeValue(planning.endingTime)}
                  onChange={updateValue(index, "endingTime")}
                />
                <InputHint>{t("activityTypeTimeInputHint")}</InputHint>
              </Field>

              <Field>
                <Label>
                  {t("activityTypeBreakTimeStart")} ({t("optional")})
                </Label>
                <TimeInput
                  type="text"
                  placeholder="12:00"
                  value={asTimeValue(planning.breakTimeStart)}
                  onChange={updateValue(index, "breakTimeStart")}
                />
                <InputHint>{t("activityTypeTimeInputHint")}</InputHint>
              </Field>

              <Field>
                <Label>
                  {t("activityTypeBreakTimeEnding")} ({t("optional")})
                </Label>
                <TimeInput
                  type="text"
                  placeholder="13:00"
                  value={asTimeValue(planning.breakTimeEnding)}
                  onChange={updateValue(index, "breakTimeEnding")}
                />
                <InputHint>{t("activityTypeTimeInputHint")}</InputHint>
              </Field>
            </Grid>

            {badRange ? <Hint>{t("activityTypePlanningInvalidRange")}</Hint> : null}
          </PlanningCard>
        );
      })}
    </Container>
  );

  function addPlanning() {
    let newPlannings = [
      ...plannings,
      {
        startingTime: null,
        endingTime: null,
        breakTimeStart: null,
        breakTimeEnding: null,
      },
    ];
    onChange(stripTempID(newPlannings));
  }

  function removePlanning(index) {
    return () => {
      let newPlannings = [...plannings];
      newPlannings.splice(index, 1);
      if (!newPlannings.length) {
        newPlannings.push({
          startingTime: null,
          endingTime: null,
          breakTimeStart: null,
          breakTimeEnding: null,
        });
      }
      onChange(stripTempID(newPlannings));
    };
  }

  function updateValue(index, field) {
    return (e) => {
      let newPlannings = [...plannings];
      let val = e.target.value;
      if (typeof val === "string") val = val.trim();
      if (!val) val = null;
      newPlannings[index] = { ...newPlannings[index], [field]: val };
      onChange(stripTempID(newPlannings));
    };
  }
}

function normalizePlannings(value) {
  if (!Array.isArray(value) || !value.length) {
    return [
      {
        startingTime: null,
        endingTime: null,
        breakTimeStart: null,
        breakTimeEnding: null,
      },
    ];
  }

  return value.map((item) => ({
    startingTime: toEditableTimeValue(item.startingTime),
    endingTime: toEditableTimeValue(item.endingTime),
    breakTimeStart: toEditableTimeValue(item.breakTimeStart),
    breakTimeEnding: toEditableTimeValue(item.breakTimeEnding),
  }));
}

function stripTempID(plannings) {
  return plannings;
}

function asTimeValue(value) {
  return value === null || value === undefined ? "" : value;
}

function toEditableTimeValue(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") return value;
  if (typeof value === "number") {
    if (Number.isNaN(value) || value < 0 || value > 23) return null;
    return `${value.toString().padStart(2, "0")}:00`;
  }
  return null;
}

function timeToMinutes(timeValue) {
  if (!timeValue || typeof timeValue !== "string") return null;
  let match = timeValue.match(/^(\d{2}):(\d{2})$/);
  if (!match) return null;
  let h = parseInt(match[1]);
  let m = parseInt(match[2]);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}
